<template>
  <div>
    <h1>Accountability</h1>
    <p class="text-muted font-weight-bold">
      State accountability indicators and compliance reporting
    </p>
    <hr />

    <!-- Stats Cards -->
    <b-row class="hover-card">
      <b-col md="4" v-for="(stat, index) in stats" :key="index" class="mb-2">
        <b-card no-body class="text-center shadow-sm hover-card h-100">
          <b-card-body
            class="d-flex flex-column align-items-center justify-content-center p-2"
          >
            <!-- Badge Type -->
            <div
              v-if="stat.type === 'badge'"
              class="rounded-circle d-flex align-items-center justify-content-center mb-1"
              :class="`bg-${stat.variant} text-${stat.textVariant}`"
              style="
                width: 60px;
                height: 60px;
                font-size: 1.5rem;
                font-weight: bold;
              "
            >
              {{ stat.value }}
            </div>

            <!-- Text Type -->
            <h1
              v-else
              class="font-weight-bolder mb-1"
              :class="`text-${stat.textVariant}`"
              style="font-size: 2.5rem"
            >
              {{ stat.value }}
            </h1>

            <h5 class="font-weight-bold text-dark mb-50">{{ stat.title }}</h5>
            <small>{{ stat.subtitle }}</small>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <b-row class="match-height">
      <!-- Indicators -->
      <b-col md="7">
        <b-card no-body class="shadow-sm">
          <b-card-header>
            <div
              class="d-flex justify-content-between align-items-center w-100"
            >
              <b-card-title>State Accountability Indicators</b-card-title>
              <b-button variant="outline-secondary" size="sm">
                <feather-icon icon="DownloadIcon" size="14" class="mr-50" />
                Export
              </b-button>
            </div>
          </b-card-header>
          <b-card-body>
            <div v-for="(item, index) in indicators" :key="index" class="mb-2">
              <div
                class="d-flex justify-content-between align-items-center mb-50"
              >
                <div class="d-flex align-items-center">
                  <feather-icon
                    :icon="item.icon"
                    size="16"
                    :class="`text-${item.variant} mr-1`"
                  />
                  <span class="font-weight-bold text-dark">{{
                    item.title
                  }}</span>
                </div>
                <div>
                  <span class="font-weight-bold">{{ item.score }}</span>
                  <span class="small"> / {{ item.target }} target</span>
                </div>
              </div>
              <b-progress
                :value="item.value"
                max="100"
                height="6px"
                variant="dark"
                class="w-100"
              />
            </div>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- Reports -->
      <b-col md="5">
        <b-card no-body class="h-100 shadow-sm">
          <b-card-header>
            <b-card-title>Compliance Reports</b-card-title>
          </b-card-header>
          <b-card-body>
            <div
              v-for="(report, index) in reports"
              :key="index"
              class="d-flex justify-content-between align-items-center p-1 mb-1 border rounded hover-items"
            >
              <div class="d-flex align-items-start">
                <div class="mr-1">
                  <feather-icon icon="FileTextIcon" size="18" />
                </div>
                <div>
                  <h6
                    class="mb-0 font-weight-bold text-dark text-wrap"
                    style="line-height: 1.2"
                  >
                    {{ report.title }}
                  </h6>
                  <small style="font-size: 0.75rem">{{ report.due }}</small>
                </div>
              </div>
              <b-badge
                pill
                :variant="report.status === 'submitted' ? 'dark' : 'light'"
                :class="report.status === 'submitted' ? '' : 'text-dark border'"
                :style="
                  report.status === 'submitted'
                    ? 'background-color: #1e1e2d'
                    : ''
                "
              >
                {{ report.status }}
              </b-badge>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BCardHeader,
  BCardTitle,
  BRow,
  BCol,
  BBadge,
  BButton,
  BProgress,
} from "bootstrap-vue";

export default {
  components: {
    BCard,
    BCardBody,
    BCardHeader,
    BCardTitle,
    BRow,
    BCol,
    BBadge,
    BButton,
    BProgress,
  },
  data() {
    return {
      stats: [
        {
          title: "District Rating",
          subtitle: "State Accountability Score: 84/100",
          value: "B+",
          type: "badge",
          variant: "light-success",
          textVariant: "success",
        },
        {
          title: "Indicators Met",
          subtitle: "2 indicators approaching target",
          value: "4/6",
          type: "text",
          textVariant: "dark",
        },
        {
          title: "Year-over-Year",
          subtitle: "Improvement in overall score",
          value: "+2.3%",
          type: "text",
          textVariant: "success",
        },
      ],
      indicators: [
        {
          title: "Academic Achievement",
          score: "73%",
          target: "75%",
          value: 73,
          icon: "AlertTriangleIcon",
          variant: "warning",
        },
        {
          title: "Academic Growth",
          score: "68%",
          target: "65%",
          value: 68,
          icon: "CheckCircleIcon",
          variant: "success",
        },
        {
          title: "Graduation Rate",
          score: "92%",
          target: "90%",
          value: 92,
          icon: "CheckCircleIcon",
          variant: "success",
        },
        {
          title: "English Learner Progress",
          score: "58%",
          target: "60%",
          value: 58,
          icon: "AlertTriangleIcon",
          variant: "warning",
        },
        {
          title: "Chronic Absenteeism",
          score: "12%",
          target: "10%",
          value: 12,
          icon: "XCircleIcon",
          variant: "danger",
        },
        {
          title: "College/Career Readiness",
          score: "54%",
          target: "50%",
          value: 54,
          icon: "CheckCircleIcon",
          variant: "success",
        },
      ],
      reports: [
        {
          title: "Annual Accountability Report",
          due: "Due: Jan 15, 2025",
          status: "submitted",
        },
        {
          title: "Title I Performance Report",
          due: "Due: Dec 20, 2024",
          status: "submitted",
        },
        {
          title: "IDEA Compliance Report",
          due: "Due: Nov 30, 2024",
          status: "submitted",
        },
        {
          title: "Civil Rights Data Collection",
          due: "Due: Mar 15, 2026",
          status: "pending",
        },
        {
          title: "EL Program Evaluation",
          due: "Due: Feb 28, 2026",
          status: "in-progress",
        },
      ],
    };
  },
};
</script>
<style>
.hover-items {
  transition: all 0.2s ease-in-out;
  border: 1px solid transparent;
}
.hover-items:hover {
  transform: translateY(-2px);
}
</style>
